import React, { useMemo } from 'react';
import { ReactSortable, SortableOptions } from 'react-sortablejs';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { IconDragVertical, IconEyeOff, IconPlus, IconTrash } from '../../icons';
import { cn } from '../../helpers/classnames';
import Tooltip from '../Tooltip';
import IconButton from '../core/IconButton';

import GroupAddPopover from './GroupAddPopover';

import useSchemaManagementDialogStore, {
  PropertyNode,
  GroupNode,
  TreeNode,
} from '../../store/schemaManagementDialog';

const messages = defineMessages({
  createNewProperty: {
    id: 'schema_management_dialog.create_new_property',
    defaultMessage: 'Create new property',
  },
  removeGroup: {
    id: 'schema_management_dialog.remove_group',
    defaultMessage: 'Remove group',
  },
  removeProperty: {
    id: 'schema_management_dialog.remove_property',
    defaultMessage: 'Remove property',
  },
});

interface NodeProps {
  node: TreeNode;
  nodeIndex: number[];
  tree: TreeNode[];
  getTree: () => TreeNode[];
  setTree: (tree: TreeNode[]) => void;
  selectedTreeNode: PropertyNode | undefined;
  setSelectedTreeNode: (selectedTreeNode: PropertyNode | undefined) => void;
  setHasChanges: (changes: boolean) => void;
  sortableOptions: SortableOptions;
  groupsLookup: Record<string, ApiEntitySchemaGroup>;
  originalPropsLookup: Record<string, ApiEntitySchemaProp>;
  propsLookup: Record<string, ApiEntitySchemaProp>;
  extrasLookup: Record<string, EntitySchemaExtra>;
  onRemoveProperty: (id: string) => void;
  onRemoveGroup: (id: number) => void;
}

const Container: React.FC<Omit<NodeProps, 'node'> & { node: GroupNode }> = ({
  node,
  nodeIndex,
  tree,
  getTree,
  setTree,
  selectedTreeNode,
  setSelectedTreeNode,
  setHasChanges,
  sortableOptions,
  groupsLookup,
  originalPropsLookup,
  propsLookup,
  extrasLookup,
  onRemoveProperty,
  onRemoveGroup,
}) => (
  <ReactSortable
    id={node.id}
    key={node.id}
    list={node.children}
    setList={(currentList) => {
      const sourceList = getTree();

      const tempList = [...sourceList];
      const group = tempList.find((x) => x.id === node.id);
      if (group) {
        group.children = currentList;
      }

      setTree(tempList);
    }}
    {...sortableOptions}
  >
    {node.children.map((child, index) => (
      <TreeNodeWrapper
        key={child.id}
        node={child}
        nodeIndex={[...nodeIndex, index]}
        tree={tree}
        getTree={getTree}
        setTree={setTree}
        selectedTreeNode={selectedTreeNode}
        setSelectedTreeNode={setSelectedTreeNode}
        setHasChanges={setHasChanges}
        sortableOptions={sortableOptions}
        groupsLookup={groupsLookup}
        originalPropsLookup={originalPropsLookup}
        propsLookup={propsLookup}
        extrasLookup={extrasLookup}
        onRemoveProperty={onRemoveProperty}
        onRemoveGroup={onRemoveGroup}
      />
    ))}
  </ReactSortable>
);

const TreeNodeWrapper: React.FC<NodeProps> = ({
  node,
  nodeIndex,
  tree,
  getTree,
  setTree,
  selectedTreeNode,
  setSelectedTreeNode,
  setHasChanges,
  sortableOptions,
  groupsLookup,
  originalPropsLookup,
  propsLookup,
  extrasLookup,
  onRemoveProperty,
  onRemoveGroup,
}) => {
  const intl = useIntl();

  if (!node) {
    return null;
  }

  return node.type === 'group' ? (
    <div id={node.id} className="p-1">
      <div className="rounded border border-background-500 bg-background-50 p-1">
        <div className="flex flex-row items-center">
          <span className="inline-flex min-w-[56px] shrink-0 text-text-700">
            <IconDragVertical className="size-6" />
          </span>
          <span className="flex-auto text-base leading-6">
            {groupsLookup[node.id.split('.')[1]]?.name}
          </span>
          <div className="mr-4 flex">
            <span className="flex-auto text-base leading-6 text-text-800">
              <FormattedMessage
                id="schema_management.group"
                defaultMessage="Group"
              />
            </span>
          </div>
          {node.children.length === 0 ? (
            <IconButton
              variant="ghost"
              icon={<IconTrash />}
              aria-label={intl.formatMessage(messages.removeGroup)}
              onClick={() => onRemoveGroup(parseInt(node.id.split('.')[1], 10))}
            />
          ) : null}
        </div>
        <div className="rounded border border-dashed border-background-500 bg-background-50 p-1">
          <Container
            node={node}
            nodeIndex={nodeIndex}
            tree={tree}
            getTree={getTree}
            setTree={setTree}
            selectedTreeNode={selectedTreeNode}
            setSelectedTreeNode={setSelectedTreeNode}
            setHasChanges={setHasChanges}
            sortableOptions={sortableOptions}
            groupsLookup={groupsLookup}
            originalPropsLookup={originalPropsLookup}
            propsLookup={propsLookup}
            extrasLookup={extrasLookup}
            onRemoveProperty={onRemoveProperty}
            onRemoveGroup={onRemoveGroup}
          />
        </div>
      </div>
    </div>
  ) : (
    <div id={node.id} className="p-1">
      <div
        className={cn(
          'flex flex-row items-center rounded border border-solid p-1',
          node.id === selectedTreeNode?.id
            ? 'border-primary-500 bg-background-200'
            : 'border-transparent bg-background-50',
        )}
      >
        <button
          type="button"
          className="flex flex-auto flex-row items-center text-left"
          onClick={() => {
            setSelectedTreeNode(node);
          }}
        >
          <span className="inline-flex min-w-[56px] shrink-0 text-text-700">
            <IconDragVertical className="size-6" />
          </span>
          <span className="flex-auto text-base leading-6">
            {propsLookup[node.id]?.name}
          </span>
          <span className="inline-flex min-w-[56px] shrink-0 text-text-700">
            {extrasLookup[node.id]?.hidden ? (
              <IconEyeOff className="size-6" />
            ) : undefined}
          </span>
        </button>
        {!originalPropsLookup[node.id] ? (
          <IconButton
            variant="ghost"
            color="red"
            icon={<IconTrash />}
            aria-label={intl.formatMessage(messages.removeProperty)}
            onClick={() => onRemoveProperty(node.id)}
          />
        ) : null}
      </div>
    </div>
  );
};

export interface SchemaManagementPropertyListProps extends SortableOptions {
  onAddProperty: () => void;
  onAddGroup: (name: string) => void;
  onRemoveProperty: (id: string) => void;
  onRemoveGroup: (id: number) => void;
}

export const SchemaManagementPropertyList: React.FC<
  SchemaManagementPropertyListProps
> = ({ onAddProperty, onAddGroup, onRemoveProperty, onRemoveGroup }) => {
  const intl = useIntl();

  const {
    tree,
    getTree,
    setTree,
    setHasChanges,
    selectedTreeNode,
    setSelectedTreeNode,
    groupsLookup,
    originalPropsLookup,
    propsLookup,
    extrasLookup,
  } = useSchemaManagementDialogStore();

  const sortableOptions = useMemo(
    (): SortableOptions => ({
      animation: 150,
      swapThreshold: 0.65,
      fallbackOnBody: true,
      group: {
        name: 'shared',
        put: (to, from, dragEl) => {
          const drag_id = dragEl.id;
          const to_id = to.el.id;

          if (drag_id.includes('group') && to_id.includes('group')) {
            return false;
          }

          return true;
        },
      },
      onEnd: () => {
        setHasChanges(true);
      },
    }),
    [setHasChanges],
  );

  return (
    <div className="flex flex-col">
      <div className="flex w-full flex-row items-center">
        <h5 className="mr-auto text-xl font-semibold text-text-950">
          <FormattedMessage
            id="schema_management_dialog.properties"
            defaultMessage="Properties"
          />
        </h5>
        <GroupAddPopover onCreate={onAddGroup} />
        <Tooltip label={intl.formatMessage(messages.createNewProperty)}>
          <IconButton
            variant="ghost"
            icon={<IconPlus />}
            aria-label={intl.formatMessage(messages.createNewProperty)}
            onClick={onAddProperty}
          />
        </Tooltip>
      </div>
      <p className="pb-1 pt-2 text-text-700">
        <FormattedMessage
          id="schema_management_dialog.dnd_instruction"
          defaultMessage="Reorder the properties by dragging them around"
        />
      </p>
      <div className="overflow-y-auto">
        <ReactSortable
          id="rs-root"
          list={tree}
          setList={setTree}
          {...sortableOptions}
        >
          {tree.map((node, index) => (
            <TreeNodeWrapper
              key={node.id}
              node={node}
              nodeIndex={[index]}
              tree={tree}
              getTree={getTree}
              setTree={setTree}
              selectedTreeNode={selectedTreeNode}
              setSelectedTreeNode={setSelectedTreeNode}
              setHasChanges={setHasChanges}
              sortableOptions={sortableOptions}
              groupsLookup={groupsLookup}
              originalPropsLookup={originalPropsLookup}
              propsLookup={propsLookup}
              extrasLookup={extrasLookup}
              onRemoveProperty={onRemoveProperty}
              onRemoveGroup={onRemoveGroup}
            />
          ))}
        </ReactSortable>
        <div id="bottom-scroll-target">&nbsp;</div>
      </div>
    </div>
  );
};

export default SchemaManagementPropertyList;
